import React, { createContext, useContext, useReducer, useEffect } from 'react'
import { useAuth } from './AuthContext'
import { getUserTypeLabel as getTypeLabel, getUserTypeColor as getTypeColor, getUserPermissions, getNormalizedUserType } from '../utils/userTypeSync'

const UserContext = createContext()

const initialState = {
  user: null,
  userType: 'guest', // 'dpo', 'it', 'admin', 'guest'
  role: null, // 'IT Officer', 'DPO', null
  agency: '',
  isAuthenticated: false,
  preferences: {
    theme: 'light',
    notifications: true,
    language: 'en'
  },
  profile: {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    department: '',
    agency: '',
    position: '',
    location: ''
  }
}

function userReducer(state, action) {
  switch (action.type) {
    case 'SET_USER':
      return {
        ...state,
        user: action.payload,
        isAuthenticated: true
      }
    case 'SET_USER_TYPE':
      return {
        ...state,
        userType: action.payload
      }
    case 'SET_ROLE':
      return {
        ...state,
        role: action.payload
      }
    case 'SET_AGENCY':
      return {
        ...state,
        agency: action.payload
      }
    case 'UPDATE_PROFILE':
      return {
        ...state,
        profile: {
          ...state.profile,
          ...action.payload
        }
      }
    case 'UPDATE_PREFERENCES':
      return {
        ...state,
        preferences: {
          ...state.preferences,
          ...action.payload
        }
      }
    case 'LOGOUT':
      return {
        ...initialState
      }
    case 'LOAD_FROM_STORAGE':
      return {
        ...state,
        ...action.payload
      }
    default:
      return state
  }
}

export function UserProvider({ children }) {
  const [state, dispatch] = useReducer(userReducer, initialState)
  const { profile } = useAuth()

  // Load user data from localStorage on mount
  useEffect(() => {
    const savedUserData = localStorage.getItem('cybergov-user')
    if (savedUserData) {
      try {
        const userData = JSON.parse(savedUserData)
        dispatch({ type: 'LOAD_FROM_STORAGE', payload: userData })
      } catch (error) {
        console.error('Error loading user data from storage:', error)
      }
    }
  }, [])

  // Save user data to localStorage whenever state changes
  useEffect(() => {
    if (state.isAuthenticated) {
      localStorage.setItem('cybergov-user', JSON.stringify(state))
    } else {
      localStorage.removeItem('cybergov-user')
    }
  }, [state])

  const setUser = (userData) => {
    dispatch({ type: 'SET_USER', payload: userData })
  }

  const setUserType = (userType) => {
    dispatch({ type: 'SET_USER_TYPE', payload: userType })
  }

  const setRole = (role) => {
    dispatch({ type: 'SET_ROLE', payload: role })
  }

  const setAgency = (agency) => {
    dispatch({ type: 'SET_AGENCY', payload: agency })
  }

  const updateProfile = (profileData) => {
    dispatch({ type: 'UPDATE_PROFILE', payload: profileData })
  }

  const updatePreferences = (preferences) => {
    dispatch({ type: 'UPDATE_PREFERENCES', payload: preferences })
  }

  const logout = () => {
    dispatch({ type: 'LOGOUT' })
  }

  const getUserTypeLabel = () => {
    const userType = getNormalizedUserType(profile, state)
    return getTypeLabel(userType)
  }

  const getUserTypeColor = () => {
    const userType = getNormalizedUserType(profile, state)
    return getTypeColor(userType)
  }

  // Role-based permission functions
  const hasRestrictedRole = () => {
    return state.role === 'IT Officer' || state.role === 'DPO'
  }

  const isGovernmentEmployee = () => {
    // Check both UserContext state and auth profile
    return state.userType === 'government_employee' || profile?.user_type === 'government_employee'
  }

  // Get permissions based on user type
  const getPermissions = () => {
    const userType = getNormalizedUserType(profile, state)
    return getUserPermissions(userType)
  }

  const canCreate = () => {
    return getPermissions().canCreate
  }

  const canEdit = () => {
    return getPermissions().canEdit
  }

  const canDelete = () => {
    return getPermissions().canDelete
  }

  const canUpdate = () => {
    return getPermissions().canEdit // Update is same as edit
  }

  const canView = () => {
    return getPermissions().canView
  }

  const canDownload = () => {
    return getPermissions().canDownload
  }

  const canManageUsers = () => {
    return getPermissions().canManageUsers
  }

  const canAccessAdmin = () => {
    return getPermissions().canAccessAdmin
  }

  const value = {
    ...state,
    setUser,
    setUserType,
    setRole,
    setAgency,
    updateProfile,
    updatePreferences,
    logout,
    getUserTypeLabel,
    getUserTypeColor,
    hasRestrictedRole,
    isGovernmentEmployee,
    canCreate,
    canEdit,
    canDelete,
    canUpdate,
    canView,
    canDownload,
    canManageUsers,
    canAccessAdmin,
    getPermissions
  }

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  )
}

export function useUser() {
  const context = useContext(UserContext)
  if (!context) {
    throw new Error('useUser must be used within a UserProvider')
  }
  return context
}

export { UserContext }
export default UserContext